import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { isNil } from 'lodash'

import './AnimatedHeight.scss'

/**
 * Shell that animates its height to follow its auto-sized content. Children
 * may swap or reflow freely; the shell eases to the new height instead of
 * jumping. Put min-heights on content INSIDE it, never on the shell itself —
 * the shell's height is measured from the content, so constraining the shell
 * would feed back into the measurement.
 *
 * @param {React.ReactNode} props.children
 */
export const AnimatedHeight = ({ children, className, ...props }) => {
  const innerRef = useRef(null)
  // null until the first ResizeObserver delivery — rendered as auto height,
  // which also keeps jsdom (inert ResizeObserver mock) on natural layout.
  const [height, setHeight] = useState(null)

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      // Deliveries can batch; only the most recent size matters.
      setHeight(entries[entries.length - 1].contentRect.height)
    })
    observer.observe(innerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      {...props}
      className={classNames('animated-height', className)}
      // Measured, truly dynamic value — the inline-style exception for values
      // that can't be expressed as a class.
      style={isNil(height) ? undefined : { height }}
    >
      <div className="animated-height__inner" ref={innerRef}>
        {children}
      </div>
    </div>
  )
}
